'use client';

import { ReactNode, useCallback } from 'react';
import clsx from 'clsx';
import { Position, PRODUCT_INFORMATION_MENU_ID } from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import {
    GlowClickable,
    GlowClickableWithRightIcon,
    GlowIcon,
    GlowTextBody,
    Icons,
    PropsWithAriaLabel,
    PropsWithClassName,
} from '@headless-workspace/glow-ds';
import fullClickable from '@headless-workspace/styles/fullClickable.module.css';
import { Component, Node } from '@headless-workspace/typings';

export type ProductDetailInfoCTAProps = PropsWithClassName &
    PropsWithAriaLabel & { menu?: Node } & (
        | {
              isHyperText?: boolean;
              labelElement: Node | string;
          }
        | {
              buttonElement: (onClick: () => void) => Node;
          }
    ) & {
        onClick?: (onOpenMenu: (menuUpdate?: ReactNode) => void) => void;
        isLoading?: boolean;
        openMenuButtonAccessibilityLabel?: string;
        modalId?: string;
    };

export const ProductDetailInfoCTA: Component<ProductDetailInfoCTAProps> = ({
    menu,
    className,
    ariaLabel,
    isLoading = false,
    onClick,
    openMenuButtonAccessibilityLabel,
    modalId,
    ...props
}) => {
    const { addAndOpen: addAndOpenMenu } = useModalActions();

    const onOpenMenu = useCallback(
        (menuUpdate?: Node) => {
            const productDetailInfosMenuClassName = 'flex flex-col bg-background-brand overflow-hidden';
            addAndOpenMenu(
                modalId ?? PRODUCT_INFORMATION_MENU_ID,
                SideMenuModal({
                    side: Position.Right,
                    map: { default: menuUpdate ?? menu },
                    className: productDetailInfosMenuClassName,
                    isOpen: false,
                    removeOnClose: true,
                }),
            );
        },
        [addAndOpenMenu, menu, modalId],
    );

    const handleClick = async () => {
        if (isLoading) {
            return;
        }
        if (onClick) {
            onClick(onOpenMenu);
        } else {
            onOpenMenu();
        }
    };

    if ('isHyperText' in props && props.isHyperText) {
        return (
            <GlowClickable
                className={'self-start'}
                onClick={handleClick}
                content={{
                    labelElement: props.labelElement,
                }}
            />
        );
    }

    if ('buttonElement' in props) {
        return props.buttonElement(handleClick);
    }

    if (typeof props.labelElement === 'string') {
        return (
            <GlowClickableWithRightIcon
                className={className}
                ariaLabel={ariaLabel}
                content={{
                    labelElement: (
                        <GlowTextBody
                            text={props.labelElement}
                            fontWeight={'medium'}
                            className={'text-wrap text-left'}
                            TagName={'span'}
                        />
                    ),
                }}
                iconClassName={isLoading ? 'animate-spin' : ''}
                rightIcon={isLoading ? Icons.Spinner : Icons.ChevronRight}
                onClick={handleClick}
            />
        );
    }

    return (
        <div
            className={clsx(
                'flex flex-row items-center justify-between w-full gap-0.5',
                className,
                fullClickable.container,
            )}
        >
            {props.labelElement}
            <GlowClickable
                className={fullClickable.element}
                content={{
                    icon: (
                        <GlowIcon
                            Icon={isLoading ? Icons.Spinner : Icons.ChevronRight}
                            type={'medium'}
                            className={clsx(isLoading && 'animate-spin')}
                        />
                    ),
                }}
                onClick={handleClick}
                ariaLabel={openMenuButtonAccessibilityLabel}
            />
        </div>
    );
};
